import React, { memo, useCallback, useContext, useEffect, useMemo } from "react";
// types
import { CouponDetails, PresentationInfo } from "@swiggy-private/cart-types";
import { CardComms, FlowType, M2CHalfcard, M2CHalfcardContext } from "@swiggy-private/super-ui";
// utils
import { getNestedValue, isEmpty } from "@swiggy-private/js-utils";
import { getUserSuperStatus } from "@cart-client/helpers/Cart";
// contexts
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartOverlaysContext } from "@swiggy-private/checkout-core";
// nr events
import { SWIGGY_ONE_COMPONENT, SWIGGY_ONE_STATE, recordSwiggyOneNREvent } from "@swiggy-private/super-ui";
// hooks
import { useM2CHalfcardAnalytics } from "@cart-client/hooks/useM2CCardAnalytics";
import { useOneMembershipPlanHandlers } from "@cart-client/hooks/useOneMembershipPlanHandlers";

interface M2CHalfcardContainerProps {
    showDelightPopup?: boolean;
    ignoreCapping?: boolean;
}

const M2CHalfcardContainer: React.FC<M2CHalfcardContainerProps> = ({ showDelightPopup, ignoreCapping }) => {
    const {
        updateCartInProgress,
        showCouponModal,
        setShowM2Chalfcard,
        setShowM2ChalfcardOnClick,
        showM2ChalfcardOnClick,
    } = useContext(UpdateCartContext);
    const { cartState } = useContext(CartContext);
    const { onCloseOverlay } = useContext(CartOverlaysContext);
    const { reviewedCart: cartResponse } = cartState;

    const swiggyPlanInfo = useMemo(
        () => getNestedValue(cartResponse, "data.swiggyOneInfo.presentationInfo", {} as PresentationInfo),
        [cartResponse],
    );

    const {
        m2c_card_comms: m2cCardComms,
        m2c_gratification_card_comms: m2cGratificationCardComms,
        plan_details: planDetails,
    } = swiggyPlanInfo;

    const userSuperStatus = useMemo(() => getUserSuperStatus(cartResponse) || "", [cartResponse]);
    const m2cHalfcardAnalytics = useM2CHalfcardAnalytics(userSuperStatus, planDetails, m2cCardComms);
    const { addSuperToCart } = useOneMembershipPlanHandlers();

    const isAutoAddCouponApplied = useMemo(() => {
        const couponDetails: CouponDetails = getNestedValue(cartResponse, "data.coupon_details");
        return !isEmpty(couponDetails?.is_coupon_auto_applied);
    }, [cartResponse]);

    const cardComms = {
        m2cCardComms,
        m2cGratificationCardComms,
        planDetails,
    };

    const isModalVisible = useMemo(
        () => isAutoAddCouponApplied || showCouponModal || showDelightPopup,
        [isAutoAddCouponApplied, showCouponModal, showDelightPopup],
    );

    useEffect(() => {
        if (isEmpty(m2cCardComms) || isEmpty(planDetails)) {
            recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.M2C_HALFCARD, SWIGGY_ONE_STATE.DATA_INVALID, {
                m2cCardCommsEmpty: String(isEmpty(m2cCardComms)),
                planDetailsEmpty: String(isEmpty(planDetails)),
            });
        } else {
            recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.M2C_HALFCARD, SWIGGY_ONE_STATE.DATA_VALID);
        }
    }, [m2cCardComms, planDetails]);

    useEffect(() => {
        if (isModalVisible) {
            recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.M2C_HALFCARD, SWIGGY_ONE_STATE.HIDDEN_BY_MODAL, {
                isAutoAddCouponApplied: String(isAutoAddCouponApplied),
                showCouponModal: String(showCouponModal),
                showDelightPopup: String(showDelightPopup),
            });
        }
    }, [isAutoAddCouponApplied, showCouponModal, showDelightPopup, isModalVisible]);

    const addSuperCallback = useCallback(
        async (flowType: FlowType) => {
            addSuperToCart(String(planDetails?.plan_id || ""), flowType, planDetails?.type);
        },
        [addSuperToCart, planDetails?.plan_id, planDetails?.type],
    );

    const onClose = useCallback(() => {
        setShowM2ChalfcardOnClick({ status: false, flowType: FlowType.M2C });
        onCloseOverlay();
    }, [onCloseOverlay, setShowM2ChalfcardOnClick]);

    return (
        <M2CHalfcardContext.Provider value={{ analyticsEvents: m2cHalfcardAnalytics }}>
            <M2CHalfcard
                cardComms={cardComms as CardComms}
                addSuper={addSuperCallback}
                hideM2CHalfcard={isModalVisible}
                updateCartInProgress={updateCartInProgress}
                setShowM2Chalfcard={setShowM2Chalfcard}
                onClose={onClose}
                ignoreCapping={ignoreCapping}
                flowType={showM2ChalfcardOnClick?.flowType}
            />
        </M2CHalfcardContext.Provider>
    );
};

export default memo(M2CHalfcardContainer);
